<template>
  <div class="quick-test">
    <p class="quick-question">{{ $t(`quickListening.${questionKey}`) }}</p>

    <div class="clip-player">
      <button
        type="button"
        class="play-button"
        :aria-label="$t(playing ? 'quickListening.pause' : 'quickListening.play')"
        @click="togglePlay"
      >
        <span class="material-icons" aria-hidden="true">{{ playing ? 'pause' : 'play_arrow' }}</span>
      </button>
      <div class="clip-track" aria-hidden="true">
        <span class="clip-progress" :style="{ width: `${progress * 100}%` }"></span>
      </div>
      <span class="clip-time">{{ formatTime(currentTime) }} / {{ formatTime(duration) }}</span>
      <audio
        ref="audio"
        :src="clip.audioUrl"
        preload="auto"
        @play="onPlay"
        @pause="playing = false"
        @ended="playing = false"
        @timeupdate="currentTime = $event.target.currentTime"
        @loadedmetadata="duration = $event.target.duration"
      ></audio>
    </div>

    <div :class="['subtitle-card', { revealed }]">
      <p class="subtitle-text" :aria-hidden="!revealed">
        <template v-for="(part, index) in textParts" :key="index">
          <mark v-if="part.word" class="subtitle-word">{{ part.text }}</mark>
          <template v-else>{{ part.text }}</template>
        </template>
      </p>
      <template v-if="revealed">
        <p class="subtitle-gloss"><span class="gloss-word">{{ clip.word }}</span>{{ clip.gloss }}</p>
        <p v-if="$i18n.locale === 'zh'" class="subtitle-translation">{{ clip.zh }}</p>
      </template>
      <p v-else class="subtitle-hint">{{ $t('quickListening.revealHint') }}</p>
    </div>

    <div class="quick-actions">
      <template v-if="!revealed">
        <button type="button" class="answer-button" :disabled="!started" @click="answer(false)">
          <span class="material-icons" aria-hidden="true">close</span>{{ $t('quickListening.no') }}
        </button>
        <button type="button" class="answer-button" :disabled="!started" @click="answer(true)">
          <span class="material-icons" aria-hidden="true">check</span>{{ $t('quickListening.yes') }}
        </button>
      </template>
      <button v-else type="button" class="primary-button" @click="advance">
        {{ $t(outcome.level ? 'quickListening.seeResult' : 'quickListening.nextClip') }}
        <span class="material-icons" aria-hidden="true">arrow_forward</span>
      </button>
    </div>
  </div>
</template>

<script>
import { QUICK_LISTENING_CLIPS } from '@/utils/quickListeningClips'
import { QUICK_START_LEVEL, pickQuickClip, quickTestStep } from '@/utils/quickListeningTest'
import { readRecentQuestions, rememberQuestions } from '@/utils/listeningTest'

export default {
  name: 'QuickListeningTest',
  emits: ['done'],
  data() {
    return {
      clip: pickQuickClip(QUICK_LISTENING_CLIPS, QUICK_START_LEVEL, readRecentQuestions()),
      answers: [],
      questionKey: 'firstQuestion',
      outcome: null,
      started: false,
      revealed: false,
      playing: false,
      currentTime: 0,
      duration: 0
    }
  },
  computed: {
    progress() {
      return this.duration ? Math.min(1, this.currentTime / this.duration) : 0
    },
    // 生词只高亮第一次出现的位置
    textParts() {
      const { text, word } = this.clip
      const index = text.toLowerCase().indexOf(word.toLowerCase())
      return [
        { text: text.slice(0, index) },
        { text: text.slice(index, index + word.length), word: true },
        { text: text.slice(index + word.length) }
      ]
    }
  },
  // 缓存里的音频可能在挂载前就读完了时长，loadedmetadata 不会再触发
  mounted() {
    const { audio } = this.$refs
    if (audio.readyState >= 1) this.duration = audio.duration
  },
  beforeUnmount() {
    this.$refs.audio?.pause()
  },
  methods: {
    formatTime(seconds) {
      const value = Math.floor(seconds || 0)
      return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`
    },
    togglePlay() {
      const audio = this.$refs.audio
      if (audio.paused) audio.play()
      else audio.pause()
    },
    onPlay() {
      this.playing = true
      this.started = true
    },
    answer(understood) {
      this.answers = [...this.answers, { level: this.clip.level, understood }]
      this.outcome = quickTestStep(this.answers)
      this.revealed = true
      // 快测公开了原文，记进听力题历史，完整测试会优先避开这些片段
      rememberQuestions([this.clip.id])
    },
    advance() {
      if (this.outcome.level) {
        this.$refs.audio.pause()
        this.$emit('done', this.outcome.level)
        return
      }
      this.questionKey = this.answers[this.answers.length - 1].understood ? 'harderQuestion' : 'easierQuestion'
      this.clip = pickQuickClip(QUICK_LISTENING_CLIPS, this.outcome.next, readRecentQuestions())
      this.outcome = null
      this.started = false
      this.revealed = false
      this.playing = false
      this.currentTime = 0
      this.duration = 0
    }
  }
}
</script>

<style scoped>
.quick-test {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.quick-question {
  margin: 0;
  color: var(--ink-primary);
  font-size: var(--font-size-xl);
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: -0.015em;
}

.clip-player {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.play-button {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background: var(--signal-active-strong);
  color: var(--color-text-inverse);
  cursor: pointer;
}

.play-button .material-icons {
  font-size: 28px;
}

.clip-track {
  position: relative;
  flex: 1;
  height: 4px;
  border-radius: var(--radius-pill);
  background: var(--border-default);
  overflow: hidden;
}

.clip-progress {
  position: absolute;
  inset: 0 auto 0 0;
  background: var(--signal-active);
}

.clip-time {
  flex-shrink: 0;
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
}

.subtitle-card {
  padding: var(--space-4) var(--space-5);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
}

.subtitle-text {
  margin: 0;
  color: var(--ink-primary);
  font-size: var(--font-size-md);
  line-height: 1.65;
  filter: blur(6px);
  user-select: none;
  transition: filter 0.35s ease;
}

.revealed .subtitle-text {
  filter: none;
  user-select: auto;
}

.subtitle-word {
  padding: 0 2px;
  border-radius: 3px;
  background: transparent;
  color: inherit;
}

.revealed .subtitle-word {
  background: var(--workspace-warm-bg);
}

.subtitle-gloss {
  display: flex;
  gap: var(--space-2);
  margin: var(--space-2) 0 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
}

.gloss-word {
  color: var(--ink-primary);
  font-weight: 500;
}

.subtitle-translation {
  margin: var(--space-3) 0 0;
  padding-top: var(--space-3);
  border-top: 1px solid var(--border-subtle);
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

.subtitle-hint {
  margin: var(--space-3) 0 0;
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.quick-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
}

.answer-button,
.primary-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-width: 120px;
  min-height: 44px;
  padding: 0 var(--space-5);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
}

.answer-button {
  border: 1px solid var(--border-default);
  background: var(--surface-panel);
  color: var(--ink-primary);
}

.answer-button:hover:not(:disabled) {
  border-color: var(--ink-tertiary);
}

.answer-button:disabled {
  background: var(--surface-subtle);
  color: var(--ink-tertiary);
  cursor: not-allowed;
}

.primary-button {
  border: 1px solid var(--ink-primary);
  background: var(--ink-primary);
  color: var(--color-text-inverse);
}

.answer-button .material-icons,
.primary-button .material-icons {
  font-size: 18px;
}

.play-button:focus-visible,
.answer-button:focus-visible,
.primary-button:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .subtitle-text {
    transition: none;
  }
}

@media (max-width: 640px) {
  .quick-actions > button {
    flex: 1;
  }
}
</style>
